import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, X, ChevronDown, ChevronUp, Lock } from 'lucide-react';

const PREFS_KEY = 'fretmanager_cookie_preferences';
// Keep legacy key for backward compat — if user already accepted, respect it
const LEGACY_KEY = 'fretmanager_privacy_accepted';

const DEFAULT_PREFS = {
  essential: true,    // Always on — auth, session, CSRF
  analytics: false,   // Page tracking, device info
  functional: false,  // Theme, language, UI preferences
};

/**
 * Read stored cookie preferences. Returns null if user hasn't decided yet.
 */
export function getCookiePreferences() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return JSON.parse(raw);
    // Legacy key exists but user never made granular choice — force re-consent
    // so they see the new preference panel with Accept/Refuse/Manage options.
  } catch { /* corrupted data */ }
  return null;
}

/**
 * Check if a specific cookie category is allowed.
 */
export function isCookieAllowed(category) {
  const prefs = getCookiePreferences();
  if (!prefs) return category === 'essential';
  return !!prefs[category];
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ ...DEFAULT_PREFS });
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const existing = getCookiePreferences();
    if (!existing) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveAndClose = useCallback((preferences) => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(preferences));
    // Clean legacy key
    localStorage.removeItem(LEGACY_KEY);
    setVisible(false);
    setShowPrefs(false);
  }, []);

  const handleAcceptAll = () => {
    saveAndClose({ essential: true, analytics: true, functional: true });
  };

  const handleRefuseAll = () => {
    saveAndClose({ essential: true, analytics: false, functional: false });
  };

  const handleSavePrefs = () => {
    saveAndClose({ ...prefs, essential: true });
  };

  const togglePref = (key) => {
    if (key === 'essential') return; // Can't disable essential
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      padding: '0 1rem 1rem',
      pointerEvents: 'none',
      animation: 'cookieSlideUp 0.4s ease-out',
    }}>
      <style>{`
        @keyframes cookieSlideUp {
          from { opacity: 0; transform: translateY(100%); }
          to { opacity: 1; transform: translateY(0); }
        }
        .cookie-toggle {
          position: relative;
          width: 40px;
          height: 22px;
          border-radius: 11px;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
          flex-shrink: 0;
        }
        .cookie-toggle::after {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: white;
          transition: transform 0.2s;
        }
        .cookie-toggle.on { background: hsl(var(--accent)); }
        .cookie-toggle.off { background: hsl(var(--muted-foreground) / 0.3); }
        .cookie-toggle.on::after { transform: translateX(18px); }
        .cookie-toggle.locked {
          background: hsl(var(--accent));
          opacity: 0.7;
          cursor: not-allowed;
        }
        .cookie-toggle.locked::after { transform: translateX(18px); }
      `}</style>

      <div style={{
        maxWidth: '640px',
        margin: '0 auto',
        background: 'hsl(var(--card))',
        border: '1px solid hsl(var(--border))',
        borderRadius: '1rem',
        padding: '1.25rem 1.5rem',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        maxHeight: '80vh',
        overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{
            flexShrink: 0,
            width: '36px',
            height: '36px',
            borderRadius: '0.625rem',
            background: 'hsl(var(--accent) / 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Shield size={18} style={{ color: 'hsl(var(--accent))' }} />
          </div>

          <div style={{ flex: 1 }}>
            <p style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'hsl(var(--foreground))',
              marginBottom: '0.25rem',
            }}>
              {t('dataProtection')}
            </p>
            <p style={{
              fontSize: '0.8rem',
              color: 'hsl(var(--muted-foreground))',
              lineHeight: 1.5,
            }}>
              {t('cookieMessage')}{' '}
              {t('cookieContinue')}{' '}
              <span
                onClick={() => navigate('/privacy')}
                style={{
                  color: 'hsl(var(--accent))',
                  cursor: 'pointer',
                  fontWeight: 600,
                  textDecoration: 'underline',
                }}
              >
                {t('cookiePrivacyPolicy')}
              </span>.
            </p>
          </div>

          <button
            onClick={() => setVisible(false)}
            style={{
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'hsl(var(--muted-foreground))',
              padding: '4px',
            }}
            aria-label={t('closeCookie')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Preference panel (collapsible) */}
        {showPrefs && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            padding: '0.75rem',
            background: 'hsl(var(--muted) / 0.3)',
            borderRadius: '0.75rem',
            border: '1px solid hsl(var(--border))',
          }}>
            {/* Essential cookies — always on */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button className="cookie-toggle locked" disabled aria-label={t('cookieEssential')}>
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Lock size={12} style={{ color: 'hsl(var(--muted-foreground))' }} />
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'hsl(var(--foreground))' }}>
                    {t('cookieEssential')}
                  </span>
                </div>
                <p style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginTop: '2px' }}>
                  {t('cookieEssentialDesc')}
                </p>
              </div>
            </div>

            {/* Analytics */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                className={`cookie-toggle ${prefs.analytics ? 'on' : 'off'}`}
                onClick={() => togglePref('analytics')}
                aria-label={t('cookieAnalytics')}
              />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'hsl(var(--foreground))' }}>
                  {t('cookieAnalytics')}
                </span>
                <p style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginTop: '2px' }}>
                  {t('cookieAnalyticsDesc')}
                </p>
              </div>
            </div>

            {/* Functional */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                className={`cookie-toggle ${prefs.functional ? 'on' : 'off'}`}
                onClick={() => togglePref('functional')}
                aria-label={t('cookieFunctional')}
              />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'hsl(var(--foreground))' }}>
                  {t('cookieFunctional')}
                </span>
                <p style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginTop: '2px' }}>
                  {t('cookieFunctionalDesc')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          justifyContent: 'flex-end',
        }}>
          {/* Manage preferences toggle */}
          <button
            onClick={() => setShowPrefs(!showPrefs)}
            style={{
              padding: '0.5rem 0.85rem',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: 'hsl(var(--muted-foreground))',
              background: 'transparent',
              border: '1px solid hsl(var(--border))',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              marginRight: 'auto',
            }}
          >
            {t('cookieManagePrefs')}
            {showPrefs ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showPrefs ? (
            /* In pref mode: Save preferences */
            <button
              onClick={handleSavePrefs}
              style={{
                padding: '0.5rem 1.25rem',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'white',
                background: 'hsl(var(--accent))',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
              }}
            >
              {t('cookieSavePrefs')}
            </button>
          ) : (
            /* Default mode: Refuse + Accept all */
            <>
              <button
                onClick={handleRefuseAll}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: 'hsl(var(--muted-foreground))',
                  background: 'transparent',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                }}
              >
                {t('cookieRefuse')}
              </button>
              <button
                onClick={handleAcceptAll}
                style={{
                  padding: '0.5rem 1.5rem',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: 'white',
                  background: 'hsl(var(--accent))',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                }}
              >
                {t('cookieAcceptAll')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
